import React, { useState, useEffect } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      }
    };
    fetchTestimonials();
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % Math.max(1, testimonials.length));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => 
      (prev - 1 + testimonials.length) % Math.max(1, testimonials.length)
    );
  };

  if (testimonials.length === 0) return null;

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section
      id="testimonials"
      className="py-20 md:py-28 bg-white"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#0F4C3A] font-semibold text-sm uppercase tracking-wider">
            Patient Stories
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
            What Our <span className="text-[#0F4C3A]">Patients Say</span>
          </h2>
          <p className="mt-6 text-lg text-gray-600">
            Real stories from real patients who have experienced our care.
          </p>
        </div>

        {/* Featured Testimonial */}
        <div className="relative max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#D1FAE5]/30 to-white rounded-3xl p-8 md:p-12 shadow-lg">
            {/* Quote Icon */}
            <div className="absolute -top-6 left-8 md:left-12">
              <div className="w-12 h-12 bg-[#0F4C3A] rounded-full flex items-center justify-center">
                <Quote className="w-6 h-6 text-white" />
              </div>
            </div>

            {/* Stars */}
            <div className="flex gap-1 mb-6 pt-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-6 h-6 ${
                    i < currentTestimonial.rating
                      ? "text-yellow-400 fill-yellow-400"
                      : "text-gray-300"
                  }`}
                />
              ))}
            </div>

            {/* Testimonial Text */}
            <blockquote className="text-xl md:text-2xl text-gray-800 leading-relaxed mb-8">
              "{currentTestimonial.text}"
            </blockquote>

            {/* Author */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-gray-900 text-lg">
                  {currentTestimonial.name}
                </p>
                <p className="text-[#0F4C3A] font-medium">
                  {currentTestimonial.service}
                </p>
                <p className="text-sm text-gray-500">{currentTestimonial.date}</p>
              </div>

              {/* Navigation */}
              <div className="flex gap-3">
                <button
                  onClick={prevSlide}
                  className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center hover:border-[#0F4C3A] hover:text-[#0F4C3A] transition-colors shadow-sm"
                  aria-label="Previous testimonial"
                  data-testid="testimonial-prev-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextSlide}
                  className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center hover:border-[#0F4C3A] hover:text-[#0F4C3A] transition-colors shadow-sm"
                  aria-label="Next testimonial"
                  data-testid="testimonial-next-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Pagination Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentIndex
                      ? "bg-[#0F4C3A] w-6"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <p className="text-4xl font-bold text-[#0F4C3A]">4.9</p>
            <p className="text-sm text-gray-500 mt-1">Google Rating</p>
          </div>
          <div>
            <p className="text-4xl font-bold text-[#0F4C3A]">5000+</p>
            <p className="text-sm text-gray-500 mt-1">Patients Treated</p>
          </div>
          <div>
            <p className="text-4xl font-bold text-[#0F4C3A]">98%</p>
            <p className="text-sm text-gray-500 mt-1">Satisfaction Rate</p>
          </div>
          <div>
            <p className="text-4xl font-bold text-[#0F4C3A]">15+</p>
            <p className="text-sm text-gray-500 mt-1">Years in Burnaby</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
